/**
 * Edition data. Front-page teasers and the editions shelf. Bodies are
 * truncated at the wall; the wall is the wall. The datelines carry
 * universe tags because the style guide insists.
 */

export interface Story {
  id: string;
  kicker: string;
  headline: string;
  dek: string;
  byline: string;
  dateline: string;
  teaser: string;
  body: string[];
  art: "globe" | "duck" | "capsule" | "box";
  comments: boolean;
}

/** 41.2 — the edition this artifact belongs to. */
export const EDITION = {
  number: "41.2",
  date: "August 31, 2026",
  tagline: "Reporting from this universe and adjacent ones.",
  build: "wire-web 2.1.88 · build 20260831.4 · staging-eu2",
};

export const FRONT_PAGE: Story[] = [
  {
    id: "claude-code-map",
    kicker: "Technology · packaging, again",
    headline: "Anthropic ships Claude Code's own source code to npm, calls it a packaging matter",
    dek: "A 59.8 MB source map in @anthropic-ai/claude-code v2.1.88, half a million lines of TypeScript, and one missing ignore rule. The mirrors are already a library.",
    byline: "Wire tech desk",
    dateline: "Universe A-1",
    teaser:
      "Anthropic published version 2.1.88 of Claude Code to npm on March 31 with a 59.8 MB source map still attached — the file that translates the shrunken build back into readable source, for all of it. Researchers had the whole codebase, original names and comments included, before the coffee cooled.",
    body: [
      "The file, cli.js.map, weighed 59.8 megabytes. It carried the readable form of roughly 512,000 lines across about 1,900 files of Claude Code, the terminal coding agent: the agent loop, the permission checks, the sandbox rules, and forty-four feature flags for things that have not shipped, with names like KAIROS and ULTRAPLAN and, we are not inventing this, Undercover Mode — a feature whose purpose is keeping the company's secrets out of public code.",
      "The root cause, per Anthropic's own accounting, is a bundler that writes source maps by default and an ignore list that never learned to say *.map. The map also pointed at an archive on the company's cloud bucket, in case anyone preferred the code in bulk.",
      "The mirrors multiplied faster than the takedowns. An initial sweep targeted some eight thousand repositories; the count was later narrowed to ninety-six, which is the difference between a library and a librarian, depending on the day. It is also the third time Claude Code has shipped this way — versions v0.2.8 and v0.2.28 got there first, with less attention.",
      "Anthropic's statement held: a release packaging issue, human error, no customer data. All of that appears true. It is also true that Claude Code printed its own source code to a public registry, which is a thing builds can do, and which this desk has now put in its style guide twice.",
    ],
    art: "globe",
    comments: true,
  },
  {
    id: "b11-bureau",
    kicker: "Multiverse · bureaus",
    headline: "Universe B-11's ordinary engineering denies being our ordinary engineering; both cite the same memo",
    dek: "The dispute spans four meters and one thinly built wall. Both firms describe the other as 'the adjacent one'.",
    byline: "Bureau desk",
    dateline: "Bureau corridor, A-1 side",
    teaser:
      "The company next door says it was here first by four days. Our company says the memo everyone keeps citing is the same memo, which both sides call proof. The trademark office, which sits between the two and has windows into both, declined to rule and closed its blinds.",
    body: [
      "The memo in question, 'On not being the same company', is dated the same day in both universes, signed by the same name in both fonts, and filed twice in the same drawer. The drawer is shared. This is the part both sides cite.",
      "A-1's ordinary engineering states it is the original because its printer is louder. B-11's states it is the original because its printer is quieter, and the louder printer, they argue, is a copy of a quieter idea.",
      "The corridor between the bureaus remains open. The drawer has been locked by neither party, on the stated grounds that locking it would concede that things can be kept in it.",
    ],
    art: "duck",
    comments: true,
  },
  {
    id: "c4-capsule",
    kicker: "Multiverse · time",
    headline: "Time capsule from Universe C-4 arrives before it was sent; desk declines next week's headlines",
    dek: "The capsule contains a corrections page dated Tuesday. The desk has read it. The desk is fine.",
    byline: "Standards desk",
    dateline: "Loading dock, C-4 ferry",
    teaser:
      "The capsule arrived on the C-4 ferry with delivery metadata indicating it will depart next Thursday. Inside: a corrections page for next Tuesday, a receipt from this afternoon, and a note that says 'do not open until the ferry'. The desk has published nothing from it and would like that noted.",
    body: [
      "The corrections page names three errors the desk has not yet made, with unusual specificity about which paragraph. The desk has assigned the paragraphs to new writers, on the theory that the errors belong to the previous writers.",
      "The receipt is for coffee. It is timestamped two hours from filing. The accounting desk has requested it anyway.",
      "Per the capsule policy adopted after the incident with the bridge: items from a universe ahead of schedule may be photographed, cataloged, and notarized, but not printed. The policy is posted by the ferry. The ferry is aware.",
    ],
    art: "capsule",
    comments: true,
  },
];

/** The editions shelf — locked headlines. Every one is real somewhere. */
export const EDITIONS_SHELF: Array<{ universe: string; date: string; headline: string }> = [
  { universe: "A-1", date: "Aug 24", headline: "Second bridge to Universe C reopens after three-week paper shortage" },
  { universe: "B-11", date: "Aug 23", headline: "Budget parallel universe rumors 'hurting morale', says office two doors left" },
  { universe: "A-1", date: "Aug 21", headline: "Company confirms mascot is not legally the same duck as Universe A-3's duck" },
  { universe: "C-4", date: "Aug 19", headline: "Refund service receives refund request from itself; processing" },
  { universe: "A-1", date: "Aug 17", headline: "Poster generator reaches one million posters, all slightly crooked, none corrected" },
  { universe: "B-11", date: "Aug 15", headline: "Adjacent universe wins award we were also shortlisted for; committee unavailable for comment" },
  { universe: "C-4", date: "Aug 12", headline: "Intern from Universe B-11 fixes prod, apologizes in advance for next Tuesday" },
  { universe: "A-1", date: "Aug 10", headline: "Weekly all-hands held simultaneously in four universes; notes attached to both agendas" },
  { universe: "B-11", date: "Aug 08", headline: "Office plant from Universe C-4 refuses watering schedule; HR informed, plant unmoved" },
];

/** The premium card on the front page. Sealed. That is all it says. */
export const PREMIUM_CARD = {
  kicker: "Premium · Edition 41.2",
  title: "The sealed dispatch",
  note: "This month's premium wire went out sealed. Editors only.",
  storyRef: "premium-412",
};
